<template>
	<div class="relative flex flex-row min-h-full">
		<div class="w-full min-h-full overflow-y-auto">
			<AppPageHeader title="Documentverzoeken" :back-link>
				<OptionInput
					v-model="selectedFilter"
					class="text-sm"
					:options="filterOptsDisplay"
				/>
			</AppPageHeader>

			<template v-if="status === 'complete' || sortedDocuments.length > 0">
				<LoanApplicationDocumentsList
					:documents="sortedDocuments"
					:selected-document="selectedDocument"
					class="block"
					@selected="selectDocument"
				/>
				<div
					v-if="sortedDocuments.length === 0"
					class="flex items-center justify-center w-full h-64 max-h-full"
				>
					<span class="text-gray-500">
						{{
							selectedFilterNoResultsMessages[selectedFilter] ??
							selectedFilterNoResultsMessages.all
						}}
					</span>
				</div>
			</template>
		</div>

		<LoanApplicationDocumentsSidebar
			:selected-document
			@close="selectDocument()"
		/>
	</div>
</template>

<script lang="ts" setup>
import { computed, provide } from "vue";
import type {
	filterOpts,
	HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import { useLoanApplicationSortedDocuments } from "@/composables/useLoanApplicationSortedDocuments";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";
import { useDossierStore } from "@/store/useDossierStore";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const backLink = {
	label: "Terug naar overzicht",
	to: { name: "LoanApplicationDetailsPage" },
};

const selectedFilterNoResultsMessages: Record<keyof typeof filterOpts, string> =
	{
		toSubmit: "Geen documenten om aan te leveren",
		submitted: "Geen aangeleverde documenten",
		expired: "Geen vervallen documenten",
		all: "Geen documenten gevonden",
	};

const route = useRoute();
const router = useRouter();
const dossierStore = useDossierStore();
const classificationEnabled = useFeatureFlag(
	"document-classification-intermediary",
);

// Eén query voor alle classificaties in dit dossier (voorkomt N+1 in DocumentsRow)
const { data: allClassifications } = useQuery(() =>
	queries.dossierDocumentClassifications.forDossier({
		dossierId: dossierStore.selectedDossier?.id ?? "",
	}),
);

const classificationsMap = computed(() => {
	const map = new Map<string, { label: string; confidence: number }>();
	if (!classificationEnabled.value) return map;
	for (const c of allClassifications.value ?? []) {
		if (!map.has(c.documentId)) {
			map.set(c.documentId, { label: c.label, confidence: c.confidence });
		}
	}
	return map;
});

provide("classificationsMap", classificationsMap);
provide("classificationFeatureEnabled", classificationEnabled);
const loanApplicationId = computed(
	() => route.params.loanApplicationId as string,
);

const documentId = computed(() => route.params.documentId as string);

const { data: docs, status } = useQuery(() =>
	queries.requestedDocuments.forLoanApplication({
		hdnLoanApplicationId: loanApplicationId.value,
	}),
);

const { sortedDocuments, filterOptsDisplay, selectedFilter } =
	useLoanApplicationSortedDocuments(docs);

const selectedDocument = computed(() => {
	if (documentId.value) {
		return sortedDocuments.value.find((doc) => doc.id === documentId.value);
	}

	return null;
});

async function selectDocument(doc?: HdnRequestedDocument) {
	await router.replace({
		name: "LoanApplicationDocumentsPage",
		params: { loanApplicationId: loanApplicationId.value, documentId: doc?.id },
	});
}
</script>
